import {
  Button,
  Divider,
  EButtonColorVariant,
  ELinkButtonVariant,
  ETextStyleVariant,
  GeneralSocialColoredAppleIcon,
  GeneralSocialColoredFacebookIcon,
  GeneralSocialColoredGoogleIcon,
  LinkButton,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { LOGIN_PAGE, SIGN_UP_PAGE } from '@/constants/urls';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { selectAuth } from '@/redux/modules/auth';
import { TAccountType } from '@/services/types/auth/ILoginForm';
import { initiateSocialAuth } from '@/utility/initiateSocialAuth';

import { getAuthErrorMessage } from './getAuthErrorMessage';
import ReCaptchaLegalNotice from './ReCaptchaLegalNotice';

interface IAuther {
  key: TAccountType;
  icon: JSX.Element;
  onClick: () => void;
  label: string;
}

const socialAuthers: IAuther[] = [
  {
    key: 'google',
    icon: <GeneralSocialColoredGoogleIcon fontSize={24} />,
    onClick: () => null,
    label: 'Google',
  },
  {
    key: 'facebook',
    icon: <GeneralSocialColoredFacebookIcon fontSize={24} />,
    onClick: () => null,
    label: 'Facebook',
  },
  {
    key: 'apple',
    icon: <GeneralSocialColoredAppleIcon fontSize={24} />,
    onClick: () => null,
    label: 'Apple',
  },
];

interface IProps {
  className?: string;
  signup?: boolean | undefined;
  onSignup?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onLogin?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  isPage?: boolean;
  didAcceptTerms?: boolean;
}

const AccountFormTemplate: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  className = '',
  onSignup,
  onLogin,
  signup,
  isPage,
  didAcceptTerms,
}) => {
  const intl = useIntl();
  const labelUsingSocialAuth = intl.formatMessage({
    defaultMessage: 'Or',
    id: '4wpUNc',
  });

  const linkLabelNoAccount = intl.formatMessage({
    defaultMessage: 'Don’t have an account yet? Join now',
    id: 'ljlE1a',
  });
  const linkLabelHaveAccount = intl.formatMessage({
    defaultMessage: 'Already have an account? Login',
    id: 'GzT4CO',
  });

  const { locale } = useLocaleSettings();

  // On login, when the "Continue with {provider}" prompt is showing, hide the footer
  // (join/login link + reCAPTCHA notice) so the prompt stands on its own. Dismissing it
  // ("Try another way") clears the error and brings the footer back.
  const authError = useSelector(selectAuth).error;
  const isSocialLoginPrompt = !signup && !!getAuthErrorMessage(intl, authError)?.socialProvider;

  const onSocialAuthClick = (method: TAccountType) => {
    initiateSocialAuth(method, locale);
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Social auth first */}
      <section className="text-center social-auth">
        <div className="flex flex-col justify-center gap-2">
          {socialAuthers.map((item: IAuther) => (
            <Button
              key={item.key}
              onClick={() => onSocialAuthClick(item.key)}
              disabled={signup && !didAcceptTerms}
              label={
                <div className="flex items-center gap-2">
                  {item.icon}
                  {signup
                    ? intl.formatMessage(
                        {
                          defaultMessage: 'Join using {provider}',
                          id: 'afCr91',
                        },
                        { provider: item.label },
                      )
                    : intl.formatMessage(
                        {
                          defaultMessage: 'Log in using {provider}',
                          id: 'GSgP99',
                        },
                        { provider: item.label },
                      )}
                </div>
              }
              className="w-full"
              variant={EButtonColorVariant.Secondary}
            />
          ))}
        </div>
        {signup && !didAcceptTerms && (
          <Text variant={ETextStyleVariant.LegalRegular} className="mt-2 text-gray-50">
            <FormattedMessage
              defaultMessage="You must accept the Outdoorsy Terms of Service before signing up with a third-party account."
              id="5ek7Lz"
            />
          </Text>
        )}
      </section>

      {/* Separator between social auth and email/password form */}
      <div className="mt-6 mb-5 text-center">
        <div className="relative flex items-center justify-center">
          <Divider className="absolute" />
          <div className="relative px-5 bg-white">
            <Text variant={ETextStyleVariant.MediumRegular} className="text-gray-50">
              {labelUsingSocialAuth}
            </Text>
          </div>
        </div>
      </div>

      {/* Email/password (or other) form content */}
      {children}

      {!isSocialLoginPrompt && (
        <>
          <Divider className="mt-8" />
          <div
            className={`flex flex-col items-center justify-center px-8  mt-6  ${
              isPage ? 'mb-12' : ''
            }`}>
            {signup ? (
              <LinkButton
                variant={ELinkButtonVariant.Underline}
                label={linkLabelHaveAccount}
                onClick={onLogin}
                href={onLogin ? '' : LOGIN_PAGE}
              />
            ) : (
              <LinkButton
                variant={ELinkButtonVariant.Underline}
                label={linkLabelNoAccount}
                onClick={onSignup}
                href={onSignup ? '' : SIGN_UP_PAGE}
              />
            )}
          </div>
          <div className="mt-6 text-center">
            <ReCaptchaLegalNotice />
          </div>
        </>
      )}
    </div>
  );
};

export { AccountFormTemplate };
